import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Heart,
  MessageCircle,
  Bookmark,
  Send,
  MoreHorizontal,
  MapPin,
  CheckCircle,
  X,
  Trees,
  Pencil,
  UserPlus,
  UserCheck,
} from 'lucide-react';
import { Stories } from './Stories';
import { ShareOptionsModal } from '../shared/ShareOptionsModal';
import { toast } from 'sonner';
import { copyToClipboard } from '../../utils/clipboard';
import type { SocialPost, SharedPlatform } from './socialTypes';
import { resolveTaggedPets } from './socialTypes';
import { PLATFORM_LABELS } from './crossPostConstants';
import { SocialPostImage } from './SocialPostImage';
import { AvatarDisplay } from './AvatarDisplay';
import { useSocialFeed } from '../../context/SocialFeedContext';
import * as socialApi from '@/api/social';
import { CreatePost } from './CreatePost';
import { ReportPostModal } from './ReportPostModal';


function formatEditedTime(
  ms: number,
  t: (key: string, opts?: Record<string, number>) => string
): string {
  const diff = Date.now() - ms;
  if (diff < 60 * 1000) return t('social.justNow');
  if (diff < 60 * 60 * 1000)
    return t('social.timeAgoM', { n: Math.floor(diff / 60000) });
  if (diff < 24 * 60 * 60 * 1000)
    return t('social.timeAgoH', { n: Math.floor(diff / 3600000) });
  return t('social.timeAgoD', { n: Math.floor(diff / 86400000) });
}

export interface ViewProfileData {
  petName: string;
  ownerName: string;
  petAvatar: string;
  ownerAvatar?: string;
  professionalBadge?: string;
  /** Clerk user ID of the profile owner (for follow API) */
  ownerId?: string;
  /** First tagged pet ID if viewing from a post (for follow pet) */
  petId?: string;
}

interface SocialFeedProps {
  userType: 'owner' | 'professional';
  userId: string;
  ownerName?: string;
  ownerAvatar?: string;
  pets: Array<{ id: string; name: string; avatar?: string; species?: string }>;
  petsForSocial: Array<{
    id: string;
    name: string;
    species?: string;
    avatar?: string;
    imageUrl?: string;
  }>;
  posts: SocialPost[];
  onPostsChange: (posts: SocialPost[]) => void;
  onViewProfile?: (profileData: ViewProfileData) => void;
  onViewPetProfile?: (petId: string) => void;
  onOpenChat?: (ownerName: string) => void;
  onFilterClick?: (
    category: 'trending' | 'nearby' | 'professionals' | 'challenges'
  ) => void;
  /** When set, Stories adds this as the user's story (e.g. from Create Post "Also share to Your Story"). */
  addToStoryMedia?: { url: string; isVideo: boolean } | null;
  onStoryAdded?: () => void;
}

export function SocialFeed({
  userType,
  userId,
  ownerName,
  ownerAvatar,
  pets,
  petsForSocial,
  posts,
  onPostsChange: _onPostsChange,
  onViewProfile,
  onViewPetProfile,
  onOpenChat: _onOpenChat,
  onFilterClick: _onFilterClick,
  addToStoryMedia,
  onStoryAdded,
}: SocialFeedProps) {
  const { t } = useTranslation();
  const {
    likePost: apiLikePost,
    unlikePost: apiUnlikePost,
    savePost: apiSavePost,
    unsavePost: apiUnsavePost,
    addComment: apiAddComment,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for delete-comment UI
    deleteComment,
    updatePost: apiUpdatePost,
    deletePost: apiDeletePost,
    isLoading: feedLoading,
    error: feedError,
    refetch: refetchFeed,
  } = useSocialFeed();
  /** Only one of the five options can be purple at a time */
  type FilterId =
    | 'following'
    | 'nearby'
    | 'professionals'
    | 'allPosts'
    | 'myPets';
  const [activeFilterId, setActiveFilterId] = useState<FilterId>('allPosts');
  const [, setActiveCategory] = useState<
    'following' | 'nearby' | 'professionals'
  >('following');
  // feedScope kept in sync with filter buttons; setter used for future scope-based filtering
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- setter used in filter buttons
  const [feedScope, setFeedScope] = useState<'allPosts' | 'myPets'>('allPosts');
  const [filterPetId, setFilterPetId] = useState<string | null>(null);
  const [showCommentsModal, setShowCommentsModal] = useState<string | null>(
    null
  );
  const [showShareModal, setShowShareModal] = useState<string | null>(null);
  const [showMoreMenu, setShowMoreMenu] = useState<string | null>(null);
  const [showReportModal, setShowReportModal] = useState<string | null>(null);
  const [newComment, setNewComment] = useState<{ [key: string]: string }>({});
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [likedCommentIds, setLikedCommentIds] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('petso_liked_comments');
      return stored ? new Set(JSON.parse(stored) as string[]) : new Set();
    } catch {
      return new Set();
    }
  });
  const commentInputRef = useRef<HTMLInputElement>(null);
  const [followedOwnerIds, setFollowedOwnerIds] = useState<Set<string>>(
    new Set()
  );
  const [followLoadingOwnerId, setFollowLoadingOwnerId] = useState<
    string | null
  >(null);

  const editingPost = editingPostId
    ? (posts.find((p) => p.id === editingPostId) ?? null)
    : null;

  // Initialize followedOwnerIds from backend
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const { following } = await socialApi.getFollowing({ limit: 500 });
        if (!cancelled) {
          setFollowedOwnerIds(new Set(following.map((f) => f.id)));
        }
      } catch {
        // silently ignore – follow buttons will default to "Follow"
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const canEditPost = useCallback(
    (post: SocialPost) => {
      if (post.ownerId !== userId) return false;
      const created = post.createdAt;
      if (created == null) return true;
      const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
      return Date.now() - created < TWENTY_FOUR_HOURS_MS;
    },
    [userId]
  );

  const handleEditPostUpdated = useCallback(
    (
      id: string,
      updates: {
        caption: string;
        taggedPetIds: string[];
        images: string[];
        location?: string;
        visibility: 'public' | 'followers' | 'friends' | 'friends-of-friends' | 'private';
      }
    ) => {
      const taggedPets = resolveTaggedPets(updates.taggedPetIds, petsForSocial);
      apiUpdatePost(
        id,
        {
          caption: updates.caption,
          taggedPetIds: updates.taggedPetIds,
          taggedPetSummaries: taggedPets,
          images: updates.images,
          location: updates.location,
          visibility: updates.visibility,
        },
        taggedPets
      );
      toast.success(t('social.postUpdated'));
      setEditingPostId(null);
    },
    [apiUpdatePost, petsForSocial, t]
  );

  const filteredPosts = useMemo(() => {
    let list: typeof posts;
    switch (activeFilterId) {
      case 'following':
        // Following: backend already returned only posts from followed users + self when scope=following
        list = posts;
        break;
      case 'nearby':
        // Nearby: client-side filter (exclude own posts for now; could use location later)
        list = posts.filter((p) => p.ownerId !== userId);
        break;
      case 'professionals':
        list = posts.filter((p) => !!p.professionalBadge);
        break;
      case 'myPets':
        list = posts.filter((p) => (p.taggedPetIds ?? []).length > 0);
        break;
      case 'allPosts':
      default:
        list = posts;
        break;
    }
    if (filterPetId)
      list = list.filter((p) => (p.taggedPetIds ?? []).includes(filterPetId));
    return list;
  }, [posts, activeFilterId, userId, filterPetId]);

  const handleLike = (postId: string) => {
    const post = posts.find((p) => p.id === postId);
    if (!post) return;
    if (post.isLiked) {
      apiUnlikePost(postId);
    } else {
      apiLikePost(postId);
    }
  };

  const handleSave = (postId: string) => {
    const post = posts.find((p) => p.id === postId);
    if (!post) return;
    if (post.isSaved) {
      apiUnsavePost(postId);
    } else {
      apiSavePost(postId);
    }
  };

  const handleAddComment = (postId: string) => {
    const commentText = newComment[postId];
    if (!commentText || commentText.trim() === '') return;
    apiAddComment(postId, commentText.trim());
    setNewComment({ ...newComment, [postId]: '' });
  };

  const handleProfileClick = (post: SocialPost) => {
    if (onViewProfile) {
      onViewProfile({
        petName: post.ownerName,
        ownerName: post.ownerName,
        petAvatar: post.ownerAvatar ?? '👤',
        ownerAvatar: post.ownerAvatar ?? undefined,
        professionalBadge: post.professionalBadge,
        ownerId: post.ownerId,
        petId: post.taggedPetIds?.[0],
      });
    }
  };

  const handleFollowOnPost = useCallback(
    async (ownerId: string, ownerName: string) => {
      if (ownerId === userId) return;
      setFollowLoadingOwnerId(ownerId);
      try {
        await socialApi.follow({ followingId: ownerId, followingType: 'USER' });
        setFollowedOwnerIds((prev) => new Set(prev).add(ownerId));
        toast.success(`Following ${ownerName}!`);
      } catch {
        toast.error(t('social.failedFollowGeneric'));
      } finally {
        setFollowLoadingOwnerId(null);
      }
    },
    [userId]
  );

  return (
    <div className="py-4 sm:py-6 px-3 sm:px-4 min-w-0 overflow-hidden">
      <div className="max-w-2xl mx-auto min-w-0">
        {/* Stories Section */}
        <div className="mb-6">
          <Stories
            userType={userType}
            userId={userId}
            pets={pets}
            addToStoryMedia={addToStoryMedia ?? undefined}
            onStoryAdded={onStoryAdded}
          />
        </div>

        {/* Feed Filters: single purple (the one user selects); content fits inside card */}
        <div className="bg-white dark:bg-[#16151D] rounded-2xl px-3 py-2.5 mb-6 border border-[#E2DFEA] dark:border-white/8 overflow-hidden">
          <div className="flex flex-wrap items-center justify-center gap-1.5">
            {[
              {
                id: 'following' as const,
                labelKey: 'social.tabFollowing',
                icon: '👥',
              },
              {
                id: 'nearby' as const,
                labelKey: 'social.tabNearby',
                icon: '📍',
              },
              {
                id: 'professionals' as const,
                labelKey: 'social.tabProfessionals',
                icon: '⭐',
              },
            ].map((filter) => (
              <button
                key={filter.id}
                onClick={() => {
                  setActiveFilterId(filter.id);
                  setActiveCategory(filter.id);
                  if (filter.id === 'following') {
                    refetchFeed('following');
                  } else {
                    setFeedScope('allPosts');
                  }
                }}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full whitespace-nowrap text-xs transition-all ${
                  activeFilterId === filter.id
                    ? 'text-white'
                    : 'bg-[#F0EFF5] dark:bg-[#0F0E16] hover:bg-[#F0EFF5] dark:hover:bg-white/5 text-[#464452] dark:text-white/70'
                }`}
                style={
                  activeFilterId === filter.id
                    ? {
                        background:
                          'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                        fontFamily: 'Inter, sans-serif',
                      }
                    : { fontFamily: 'Inter, sans-serif' }
                }
              >
                <span>{filter.icon}</span>
                <span>{t(filter.labelKey)}</span>
              </button>
            ))}
            <button
              onClick={() => {
                setActiveFilterId('allPosts');
                setFeedScope('allPosts');
                refetchFeed('all');
              }}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full whitespace-nowrap text-xs transition-all ${
                activeFilterId === 'allPosts'
                  ? 'text-white'
                  : 'bg-[#F0EFF5] dark:bg-[#0F0E16] hover:bg-[#F0EFF5] dark:hover:bg-white/5 text-[#464452] dark:text-white/70'
              }`}
              style={
                activeFilterId === 'allPosts'
                  ? {
                      background:
                        'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                      fontFamily: 'Inter, sans-serif',
                    }
                  : { fontFamily: 'Inter, sans-serif' }
              }
            >
              <span>🐾</span>
              <span>{t('social.allPosts')}</span>
            </button>
            <button
              onClick={() => {
                setActiveFilterId('myPets');
                setFeedScope('myPets');
                refetchFeed('myPets');
              }}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full whitespace-nowrap text-xs transition-all ${
                activeFilterId === 'myPets'
                  ? 'text-white'
                  : 'bg-[#F0EFF5] dark:bg-[#0F0E16] hover:bg-[#F0EFF5] dark:hover:bg-white/5 text-[#464452] dark:text-white/70'
              }`}
              style={
                activeFilterId === 'myPets'
                  ? {
                      background:
                        'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                      fontFamily: 'Inter, sans-serif',
                    }
                  : { fontFamily: 'Inter, sans-serif' }
              }
            >
              <span>👥</span>
              <span>{t('social.myPets')}</span>
            </button>
            <div className="flex items-center gap-1">
              <span
                className="text-xs text-[#464452] dark:text-white/60 whitespace-nowrap"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('social.petFilterLabel')}
              </span>
              <select
                value={filterPetId ?? ''}
                onChange={(e) => setFilterPetId(e.target.value || null)}
                className="px-2 py-1.5 rounded-full border border-[#E2DFEA] dark:border-white/20 bg-white dark:bg-[#0F0E16] text-[#464452] dark:text-white/90 text-xs w-auto max-w-[90px]"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <option value="">{t('social.all')}</option>
                {petsForSocial.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Loading / Error */}
        {feedLoading && (
          <div className="bg-white dark:bg-[#16151D] rounded-2xl p-12 border border-[#E2DFEA] dark:border-white/8 text-center">
            <div className="animate-pulse text-[#464452] dark:text-white/70">
              {t('social.loadingFeed')}
            </div>
          </div>
        )}
        {feedError && !feedLoading && (
          <div className="bg-white dark:bg-[#16151D] rounded-2xl p-6 border border-[#E2DFEA] dark:border-white/8">
            <p className="text-red-500 dark:text-red-400 mb-4">{feedError}</p>
            <button
              onClick={() => refetchFeed()}
              className="px-4 py-2 rounded-lg bg-[#936FEF] text-white font-medium"
            >
              {t('social.retry')}
            </button>
          </div>
        )}

        {/* Posts Feed */}
        <div className="space-y-6">
          {!feedLoading && !feedError && filteredPosts.length === 0 && (
            <div className="bg-white dark:bg-[#16151D] rounded-2xl p-12 border border-[#E2DFEA] dark:border-white/8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#F0EFF5] dark:bg-white/10 text-[#936FEF] mb-4 text-3xl">
                🐾
              </div>
              <h3
                className="text-lg font-semibold text-[#2D2F7E] dark:text-white mb-2"
                style={{ fontFamily: 'Sora, sans-serif' }}
              >
                {t('social.noPostsYet')}
              </h3>
              <p
                className="text-[#464452] dark:text-white/70 text-sm max-w-sm mx-auto"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('social.beTheFirstToShare')}
              </p>
            </div>
          )}
          {!feedLoading &&
            !feedError &&
            filteredPosts.map((post) => (
              <div
                key={post.id}
                className="bg-white dark:bg-[#16151D] rounded-2xl overflow-hidden border border-[#E2DFEA] dark:border-white/8"
              >
                {/* Post Header: Owner-based */}
                <div className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3 flex-wrap">
                    <button
                      onClick={() => handleProfileClick(post)}
                      className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                    >
                      <AvatarDisplay
                        src={post.ownerAvatar}
                        alt={post.ownerName}
                        fallback="👤"
                        className="text-2xl"
                        containerClassName="w-10 h-10"
                      />
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <span
                            className="font-semibold text-[#2D2F7E] dark:text-white"
                            style={{ fontFamily: 'Sora, sans-serif' }}
                          >
                            {post.ownerName}
                          </span>
                          {post.verified && (
                            <CheckCircle className="w-4 h-4 text-[#936FEF] fill-[#936FEF]" />
                          )}
                          {post.professionalBadge && (
                            <span
                              className="px-2 py-0.5 rounded-full text-xs bg-[#F0EFF5] dark:bg-purple-900/20 text-[#936FEF] dark:text-purple-300"
                              style={{ fontFamily: 'Inter, sans-serif' }}
                            >
                              {post.professionalBadge}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                    {/* Tagged pets: "With Max, Bella" or avatars (click → pet profile) */}
                    {(post.taggedPets ?? []).length > 0 && (
                      <div className="flex items-center gap-1.5 ml-2 flex-wrap">
                        <span
                          className="text-xs text-[#464452] dark:text-white/50"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          {t('social.with')}
                        </span>
                        {(post.taggedPets ?? []).map((pet) => (
                          <button
                            key={pet.id}
                            type="button"
                            onClick={() => onViewPetProfile?.(pet.id)}
                            className="flex items-center gap-1 rounded-full pl-1 pr-2 py-0.5 hover:bg-[#F0EFF5] dark:hover:bg-white/10 transition-colors"
                            title={`View ${pet.name}`}
                          >
                            <span className="w-6 h-6 rounded-full flex items-center justify-center text-sm bg-[#F0EFF5] dark:bg-white/10">
                              {pet.avatar}
                            </span>
                            <span
                              className="text-xs font-medium text-[#2D2F7E] dark:text-white"
                              style={{ fontFamily: 'Inter, sans-serif' }}
                            >
                              {pet.name}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {post.ownerId && post.ownerId !== userId && (
                      <button
                        type="button"
                        onClick={() =>
                          handleFollowOnPost(post.ownerId, post.ownerName)
                        }
                        disabled={
                          followLoadingOwnerId === post.ownerId ||
                          followedOwnerIds.has(post.ownerId)
                        }
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors disabled:opacity-70"
                        style={{
                          fontFamily: 'Inter, sans-serif',
                          ...(followedOwnerIds.has(post.ownerId)
                            ? {
                                background: 'rgba(240, 239, 245, 0.9)',
                                color: 'var(--color-text-secondary, #464452)',
                              }
                            : {
                                background:
                                  'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                                color: 'white',
                              }),
                        }}
                      >
                        {followedOwnerIds.has(post.ownerId) ? (
                          <>
                            <UserCheck className="w-3.5 h-3.5" />
                            {t('social.following')}
                          </>
                        ) : (
                          <>
                            <UserPlus className="w-3.5 h-3.5" />
                            {t('social.follow')}
                          </>
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => setShowMoreMenu(post.id)}
                      className="hover:bg-[#F0EFF5] dark:hover:bg-white/5 p-2 rounded-lg transition-colors text-[#464452] dark:text-white/70"
                    >
                      <MoreHorizontal className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Post content: image/video or text-only (tweet style) */}
                {post.images?.length > 0 ? (
                  <div className="relative w-full aspect-square bg-[#F0EFF5] dark:bg-white/10">
                    <SocialPostImage
                      src={post.images[0]}
                      alt={post.ownerName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="px-4 py-4 bg-[#F0EFF5] dark:bg-white/5 border-t border-b border-[#E2DFEA] dark:border-white/8">
                    <p
                      className="text-[#2D2F7E] dark:text-white whitespace-pre-wrap break-words"
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '15px',
                        lineHeight: 1.4,
                      }}
                    >
                      {post.caption || ' '}
                    </p>
                  </div>
                )}

                {/* Post Actions */}
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => handleLike(post.id)}
                        className="flex items-center gap-1 group"
                      >
                        <Heart
                          className={`w-6 h-6 transition-colors ${
                            post.isLiked
                              ? 'text-[#936FEF] fill-[#936FEF]'
                              : 'text-[#464452] dark:text-white/70 group-hover:text-[#936FEF]'
                          }`}
                        />
                      </button>
                      <button
                        onClick={() => setShowCommentsModal(post.id)}
                        className="text-[#464452] dark:text-white/70 hover:text-[#936FEF] transition-colors"
                      >
                        <MessageCircle className="w-6 h-6" />
                      </button>
                      <button
                        onClick={() => setShowShareModal(post.id)}
                        className="text-[#464452] dark:text-white/70 hover:text-[#936FEF] transition-colors"
                      >
                        <Send className="w-6 h-6" />
                      </button>
                    </div>
                    <button
                      onClick={() => handleSave(post.id)}
                      className="text-[#464452] dark:text-white/70 hover:text-[#936FEF] transition-colors"
                    >
                      <Bookmark
                        className={`w-6 h-6 ${post.isSaved ? 'fill-[#464452] dark:fill-white/70' : ''}`}
                      />
                    </button>
                  </div>

                  {/* Likes Count */}
                  <div className="mb-2">
                    <span
                      className="font-semibold text-[#2D2F7E] dark:text-white"
                      style={{ fontFamily: 'Sora, sans-serif' }}
                    >
                      {(post.likes ?? 0).toLocaleString()} {t('social.like')}
                    </span>
                  </div>

                  {/* Comments Preview */}
                  {(post.comments ?? []).length > 0 && (
                    <button
                      onClick={() => setShowCommentsModal(post.id)}
                      className="text-sm mb-2 hover:text-[#936FEF] transition-colors text-[#464452] dark:text-white/50"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      View all {(post.comments ?? []).length} comments
                    </button>
                  )}

                  {/* Add Comment */}
                  <div className="flex items-center gap-3 pt-3 border-t border-[#E2DFEA] dark:border-white/8">
                    <input
                      type="text"
                      placeholder={t('social.addComment')}
                      value={newComment[post.id] || ''}
                      onChange={(e) =>
                        setNewComment({
                          ...newComment,
                          [post.id]: e.target.value,
                        })
                      }
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleAddComment(post.id);
                        }
                      }}
                      className="flex-1 text-sm outline-none bg-transparent text-[#464452] dark:text-white/70 placeholder:text-[#464452] dark:placeholder:text-white/50"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    />
                    <button
                      onClick={() => handleAddComment(post.id)}
                      disabled={
                        !newComment[post.id] ||
                        newComment[post.id].trim() === ''
                      }
                      className="text-sm font-semibold hover:opacity-80 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{
                        color: '#936FEF',
                        fontFamily: 'Sora, sans-serif',
                      }}
                    >
                      Post
                    </button>
                  </div>
                </div>
              </div>
            ))}
        </div>

        {/* Load More */}
        {filteredPosts.length > 0 && (
          <div className="mt-6 text-center">
            <button
              className="px-6 py-2 rounded-xl bg-white dark:bg-[#0F0E16] border border-[#E2DFEA] dark:border-white/20 text-[#464452] dark:text-white/70 hover:bg-[#F0EFF5] dark:hover:bg-white/5 transition-colors"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Load More Posts
            </button>
          </div>
        )}
      </div>

      {/* Comments Modal */}
      {showCommentsModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#16151D] rounded-2xl border dark:border-white/8 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 sticky top-0 bg-white dark:bg-[#16151D] z-10 rounded-t-2xl border-b border-[#E2DFEA] dark:border-white/8">
              <h2
                className="text-xl text-[#2D2F7E] dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {t('social.comments')}
              </h2>
              <button
                onClick={() => setShowCommentsModal(null)}
                className="hover:bg-[#F0EFF5] dark:hover:bg-white/5 p-2 rounded-lg transition-colors text-[#464452] dark:text-white/70"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Comments List */}
            <div className="p-4 space-y-4">
              {(() => {
                const modalComments = posts.find(
                  (p) => p.id === showCommentsModal
                )?.comments;
                if (!modalComments?.length) {
                  return (
                    <p
                      className="text-sm text-[#464452] dark:text-white/50 text-center py-6"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('social.noCommentsYet')}
                    </p>
                  );
                }
                return modalComments.map((comment) => (
                  <div key={comment.id} className="flex gap-3">
                    <AvatarDisplay
                      src={comment.avatar}
                      alt={comment.author || 'User'}
                      fallback="👤"
                      className="text-lg"
                      containerClassName="w-9 h-9"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span
                          className="font-bold text-sm text-[#2D2F7E] dark:text-white"
                          style={{ fontFamily: 'Sora, sans-serif' }}
                        >
                          {comment.author || 'User'}
                        </span>
                        <span
                          className="text-xs text-[#464452]/60 dark:text-white/40"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          {comment.timestamp}
                        </span>
                      </div>
                      <p
                        className="text-sm text-[#464452] dark:text-white/80 mt-0.5 break-words"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {comment.text}
                      </p>
                      <div className="flex items-center gap-4 mt-1">
                        <button
                          onClick={() => {
                            setLikedCommentIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(comment.id)) next.delete(comment.id);
                              else next.add(comment.id);
                              try { localStorage.setItem('petso_liked_comments', JSON.stringify([...next])); } catch { /* ignore */ }
                              return next;
                            });
                          }}
                          className={`text-xs font-semibold transition-colors ${
                            likedCommentIds.has(comment.id)
                              ? 'text-[#936FEF]'
                              : 'text-[#464452] dark:text-white/50 hover:text-[#936FEF]'
                          }`}
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          {likedCommentIds.has(comment.id) ? t('social.liked') : t('social.like')}
                        </button>
                        <button
                          onClick={() => commentInputRef.current?.focus()}
                          className="text-xs font-semibold text-[#464452] dark:text-white/50 hover:text-[#936FEF] transition-colors"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          {t('social.reply')}
                        </button>
                      </div>
                    </div>
                  </div>
                ));
              })()}
            </div>

            {/* Add Comment (in modal) */}
            <div className="p-4 sticky bottom-0 bg-white dark:bg-[#16151D] rounded-b-2xl border-t border-[#E2DFEA] dark:border-white/8">
              <div className="flex items-center gap-3">
                <input
                  ref={commentInputRef}
                  type="text"
                  placeholder={t('social.addComment')}
                  value={newComment[showCommentsModal] || ''}
                  onChange={(e) =>
                    setNewComment({
                      ...newComment,
                      [showCommentsModal]: e.target.value,
                    })
                  }
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddComment(showCommentsModal);
                    }
                  }}
                  className="flex-1 px-4 py-2 rounded-xl outline-none border border-[#E2DFEA] dark:border-white/20 bg-white dark:bg-[#0F0E16] text-[#464452] dark:text-white/90 placeholder:text-[#464452]/50 dark:placeholder:text-white/50"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                />
                <button
                  onClick={() => handleAddComment(showCommentsModal)}
                  disabled={
                    !newComment[showCommentsModal] ||
                    newComment[showCommentsModal].trim() === ''
                  }
                  className="px-4 py-2 rounded-xl text-white hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    background:
                      'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 700,
                  }}
                >
                  Post
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <ShareOptionsModal
          isOpen={true}
          onClose={() => setShowShareModal(null)}
          title={t('social.sharePost')}
          message={`Check out this post from ${posts.find((p) => p.id === showShareModal)?.petName}!`}
          url={`https://petso.io/post/${showShareModal}`}
          shareType="general"
        />
      )}

      {/* Edit Post Modal – reuses full CreatePost in edit mode */}
      {editingPost && (
        <CreatePost
          userType={userType}
          userId={userId}
          ownerName={ownerName}
          ownerAvatar={ownerAvatar}
          pets={petsForSocial}
          onClose={() => setEditingPostId(null)}
          onPostCreated={() => {}}
          editingPost={{
            id: editingPost.id,
            images: editingPost.images,
            caption: editingPost.caption,
            taggedPetIds: editingPost.taggedPetIds ?? [],
            location: editingPost.location,
            visibility: (editingPost.visibility as 'public' | 'followers' | 'friends' | 'friends-of-friends' | 'private') ?? 'public',
          }}
          onPostUpdated={handleEditPostUpdated}
        />
      )}

      {/* More Options Menu */}
      {showMoreMenu && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowMoreMenu(null)}
        >
          <div
            className="bg-white dark:bg-[#16151D] rounded-2xl border dark:border-white/8 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-2">
              {(() => {
                const post = posts.find((p) => p.id === showMoreMenu);
                const isOwn = post?.ownerId === userId;
                const withinEditWindow = post && canEditPost(post);
                return (
                  <>
                    {isOwn && withinEditWindow && (
                      <button
                        onClick={() => {
                          setEditingPostId(showMoreMenu);
                          setShowMoreMenu(null);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-[#F0EFF5] dark:hover:bg-white/5 transition-colors"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        <Pencil className="w-5 h-5 text-[#936FEF]" />
                        <span className="text-[#2D2F7E] dark:text-white">
                          Edit Post
                        </span>
                      </button>
                    )}
                    {isOwn && (
                      <button
                        onClick={() => {
                          apiDeletePost(showMoreMenu);
                          toast.success(t('social.postDeleted'));
                          setShowMoreMenu(null);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-[#F0EFF5] dark:hover:bg-white/5 transition-colors"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        <span className="text-[#FF4444]">🗑️ Delete Post</span>
                      </button>
                    )}
                  </>
                );
              })()}
              <button
                onClick={() => {
                  toast.success(t('social.copyingLink'));
                  const link = `https://petso.io/post/${showMoreMenu}`;
                  copyToClipboard(link).then(() => {
                    toast.success(t('social.linkCopied'));
                  });
                  setShowMoreMenu(null);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-[#F0EFF5] dark:hover:bg-white/5 transition-colors"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <span className="text-[#464452] dark:text-white/70">
                  🔗 Copy Link
                </span>
              </button>
              <button
                onClick={() => {
                  toast.success(t('social.savingPost'));
                  handleSave(showMoreMenu);
                  setShowMoreMenu(null);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-[#F0EFF5] dark:hover:bg-white/5 transition-colors"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <span className="text-[#464452] dark:text-white/70">
                  🔖 Save Post
                </span>
              </button>
              <button
                onClick={() => {
                  toast.info(t('social.demoAction'));
                  setShowMoreMenu(null);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-[#F0EFF5] dark:hover:bg-white/5 transition-colors"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <span className="text-[#464452] dark:text-white/70">
                  🚫 Not Interested
                </span>
              </button>
              <button
                onClick={() => {
                  setShowReportModal(showMoreMenu);
                  setShowMoreMenu(null);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-[#F0EFF5] dark:hover:bg-white/5 transition-colors"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <span className="text-[#FF4444]">⚠️ Report Post</span>
              </button>
              <button
                onClick={() => setShowMoreMenu(null)}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl hover:bg-[#F0EFF5] dark:hover:bg-white/5 transition-colors mt-2 text-[#464452] dark:text-white/70"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Post Modal */}
      <ReportPostModal
        isOpen={!!showReportModal}
        postId={showReportModal ?? ''}
        onClose={() => setShowReportModal(null)}
      />
    </div>
  );
}
